import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Bot, CalendarCheck2, CheckCircle2, CreditCard, PlayCircle, Shield, Sparkles, Wand2
} from 'lucide-react';
import { toast } from './ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAPABILITY_CARDS = [
  { id: 'agents', title: 'Agentes activos', description: 'Ventas, contenido, soporte e integraciones listas para activar.', icon: Bot },
  { id: 'video', title: 'Vídeo + repurpose', description: 'Piezas listas para Shorts, Reels, TikTok y YouTube.', icon: PlayCircle },
  { id: 'publishing', title: 'Publicación', description: 'Calendario, aprobaciones y salidas multicanal según el plan.', icon: CalendarCheck2 },
  { id: 'security', title: 'Seguridad', description: 'Roles, trazabilidad, privacidad y pagos seguros.', icon: Shield },
];

const PLAN_COPY_OVERRIDES = {
  neuralia_core: {
    badge: 'Base ejecutiva',
    shortName: 'Core',
    shortTagline: 'Entrada premium para una unidad con foco, orden y seguimiento.',
    shortPositioning: 'Base operativa',
    shortAudience: '1 unidad · comercial / marketing',
  },
  neuralia_growth: {
    badge: 'Más equilibrado',
    shortName: 'Growth',
    shortTagline: 'Escala varias áreas con más control, ritmo y trazabilidad.',
    shortPositioning: 'Escalado guiado',
    shortAudience: '2–4 áreas / equipos',
  },
  neuralia_scale: {
    badge: 'Capa premium',
    shortName: 'Group',
    shortTagline: 'Coordina varias unidades con visión ejecutiva y producción alineada.',
    shortPositioning: 'Dirección multiunidad',
    shortAudience: 'Multi-marca / expansión',
  },
};

export const SaaSPricingHub = ({ token, email, setEmail }) => {
  const [plans, setPlans] = useState([]);
  const [securityPosture, setSecurityPosture] = useState(null);
  const [promo, setPromo] = useState(null);
  const [marketPositioning, setMarketPositioning] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loadingPlan, setLoadingPlan] = useState(null);

  const load = useCallback(async () => {
    try {
      const [plansRes, securityRes] = await Promise.all([
        fetch(`${API}/subscription-plans`),
        fetch(`${API}/security/posture`),
      ]);
      const plansData = await plansRes.json();
      const securityData = await securityRes.json();
      setPlans(plansData.plans || []);
      setPromo(plansData.promo || null);
      setMarketPositioning(plansData.market_positioning || '');
      setSecurityPosture(securityData);
    } catch (error) {
      console.error('Error loading SaaS pricing hub:', error);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const annualLabel = useMemo(() => plans[0]?.annual_discount_percent || 17, [plans]);

  const getPricingView = (plan) => {
    const isAnnual = billingCycle === 'annual';
    return {
      headlinePrice: isAnnual ? plan.annual_monthly_equivalent : plan.monthly_price,
      headlineOriginal: isAnnual ? plan.annual_original_monthly_equivalent : plan.monthly_original_price,
      cycleTotal: isAnnual ? plan.annual_price : plan.monthly_price,
      cycleSavings: isAnnual ? plan.annual_launch_savings : plan.monthly_launch_savings,
    };
  };

  const getPlanDisplay = (plan) => {
    const overrides = PLAN_COPY_OVERRIDES[plan.id] || {};
    return {
      badge: overrides.badge || plan.positioning,
      name: overrides.shortName || plan.name,
      audience: overrides.shortAudience || plan.recommended_for,
      tagline: overrides.shortTagline || plan.tagline,
      positioning: overrides.shortPositioning || plan.positioning,
      deliveryModel: plan.delivery_model,
      features: (plan.features || []).slice(0, 4),
    };
  };

  const handleSubscribe = async (planId) => {
    setLoadingPlan(planId);
    try {
      const response = await fetch(`${API}/subscription-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          plan_id: planId,
          billing_cycle: billingCycle,
          origin_url: window.location.origin,
          email: email || null,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'No se pudo iniciar la suscripción');
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message || 'No se pudo iniciar la suscripción');
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <section className="pb-golden-xl" data-testid="saas-pricing-hub">
      <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg">
        <div className="rounded-[20px] border border-primary/15 bg-[radial-gradient(circle_at_top_left,rgba(109,74,255,0.12),transparent_38%),radial-gradient(circle_at_bottom_right,rgba(196,189,244,0.14),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.88),rgba(255,255,255,0.72))] p-6 md:p-8 lg:p-10 shadow-[0_24px_80px_rgba(18,18,18,0.08)]">
          <div className="grid gap-8 xl:grid-cols-[0.9fr_1.1fr] xl:items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-2" data-testid="saas-badge">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Director IA · capa premium</span>
              </div>
              <h2 className="mt-5 max-w-3xl text-4xl sm:text-5xl leading-tight">La capa ejecutiva para operar con más control, continuidad y calma.</h2>
              <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-slate-700" data-testid="saas-value-prop">
                Ordena ventas, contenido, soporte y operación en una sola capa de dirección. Más claridad para decidir, mejor seguimiento y una estructura que aguanta el crecimiento. {marketPositioning}
              </p>

              {promo?.active && (
                <div className="mt-6 rounded-[24px] border border-accent/25 bg-accent/8 p-4" data-testid="saas-launch-banner">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-accent">Integración de lanzamiento</p>
                  <p className="mt-2 text-xl font-semibold text-text-heading">{promo.discount_percent}% OFF para la primera integración — plazas limitadas.</p>
                  <p className="mt-2 text-sm text-slate-700">Activa ahora y ahorra desde el primer mes. Quedan {promo.remaining_slots} activaciones de {promo.total_slots}.</p>
                </div>
              )}

              <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="saas-capabilities-grid">
                {CAPABILITY_CARDS.map((item) => (
                  <div key={item.id} className="rounded-[16px] border border-border-subtle bg-white/84 p-4" data-testid={`saas-capability-${item.id}`}>
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><item.icon className="h-5 w-5" /></div>
                    <h3 className="mt-4 text-base font-semibold text-text-heading">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-[16px] border border-border-subtle bg-white/84 p-4" data-testid="saas-billing-toggle-card">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-slate-600">Facturación</p>
                  <p className="mt-2 text-sm text-slate-700">Incluye onboarding guiado y comité de seguimiento. Puedes activar pago mensual o anual.</p>
                </div>
                <div className="inline-flex rounded-full border border-border-subtle bg-background/70 p-1" data-testid="saas-billing-toggle">
                  <button onClick={() => setBillingCycle('monthly')} className={`rounded-full px-4 py-2 text-sm transition-colors ${billingCycle === 'monthly' ? 'bg-primary text-background' : 'text-slate-700'}`} data-testid="saas-billing-monthly">Mensual</button>
                  <button onClick={() => setBillingCycle('annual')} className={`rounded-full px-4 py-2 text-sm transition-colors ${billingCycle === 'annual' ? 'bg-primary text-background' : 'text-slate-700'}`} data-testid="saas-billing-annual">Anual · -{annualLabel}%</button>
                </div>
              </div>

              <div className="rounded-[16px] border border-border-subtle bg-white/84 p-4" data-testid="saas-email-card">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-slate-600 mb-2">Email para la suscripción</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} className="input-kinesthetic" placeholder="tu@email.com" data-testid="saas-email-input" />
              </div>

              <div className="rounded-[16px] border border-primary/15 bg-primary/5 p-4" data-testid="saas-value-note">
                <p className="text-sm text-slate-700">Reserva tu plaza. Si hoy te encaja mejor una ruta más simple, te orientamos primero con consultoría o servicio directo.</p>
              </div>

              <div className="grid gap-4 md:grid-cols-3" data-testid="saas-plan-grid">
                {plans.slice(0, 3).map((plan, index) => {
                  const featured = plan.id === 'neuralia_growth';
                  const premium = plan.id === 'neuralia_scale';
                  const pricing = getPricingView(plan);
                  const display = getPlanDisplay(plan);
                  const showOriginalPrice = Number(pricing.headlineOriginal) > Number(pricing.headlinePrice);
                  const showSavings = Number(pricing.cycleSavings) > 0;
                  return (
                    <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.06 }} className={`rounded-[18px] border p-5 backdrop-blur-xl ${featured ? 'border-primary bg-primary/6 shadow-[0_20px_60px_rgba(109,74,255,0.10)]' : 'border-border-subtle bg-white/84'} ${premium ? 'border-primary/25 bg-[linear-gradient(180deg,rgba(109,74,255,0.05),rgba(255,255,255,0.88))]' : ''}`} data-testid={`saas-plan-card-${plan.id}`}>
                      <div className="mb-4 flex flex-wrap gap-2">
                        {(featured || premium || display.badge) && (
                          <div className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${premium ? 'border border-accent/30 bg-accent/10 text-accent' : featured ? 'bg-primary text-background' : 'border border-primary/20 bg-primary/10 text-primary'}`}>
                            {display.badge}
                          </div>
                        )}
                        {plan.promo_active && <div className="inline-flex rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-accent">-{plan.launch_discount_percent}%</div>}
                      </div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-600">{display.audience}</p>
                      <h3 className="mt-3 text-2xl font-semibold text-text-heading">{display.name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-700">{display.tagline}</p>
                      <p className="mt-3 text-[10px] uppercase tracking-[0.18em] text-primary">{display.positioning} · {display.deliveryModel}</p>

                      <div className="mt-5 flex items-end gap-2">
                        <span className="text-4xl font-bold text-text-heading">${pricing.headlinePrice}</span>
                        <span className="pb-1 text-sm text-slate-600">USD / mes</span>
                      </div>
                      {(showOriginalPrice || showSavings) && (
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                          {showOriginalPrice && <span className="text-slate-600 line-through">Antes ${pricing.headlineOriginal}/mes</span>}
                          {showSavings && <span className="text-accent font-semibold">Ahorras ${pricing.cycleSavings} {billingCycle === 'annual' ? 'al año' : '/ mes'}</span>}
                        </div>
                      )}
                      {billingCycle === 'annual' && (
                        <p className="mt-2 text-xs text-emerald-300" data-testid={`saas-plan-savings-${plan.id}`}>Pago anual: ${plan.annual_price} · ahorras ${plan.annual_savings}</p>
                      )}

                      <div className="mt-5 space-y-3">
                        {display.features.map((feature) => (
                          <div key={feature} className="flex items-start gap-2 text-sm text-slate-700">
                            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-5 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-primary">Dirección IA incluida</span>
                        {plan.mlm_ready && <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-emerald-300">Red preparada</span>}
                      </div>

                      <button onClick={() => handleSubscribe(plan.id)} disabled={loadingPlan === plan.id} className={`mt-6 w-full ${featured ? 'btn-primary' : 'btn-secondary'}`} data-testid={`saas-subscribe-button-${plan.id}`}>
                        {loadingPlan === plan.id ? <><Wand2 className="h-4 w-4 animate-spin" /> Procesando...</> : <><CreditCard className="h-4 w-4" /> {premium ? 'Activar y reservar' : 'Activar ahora'}</>}
                      </button>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {securityPosture && (
          <div className="mt-8 grid gap-6 xl:grid-cols-[0.9fr_1.1fr]" data-testid="saas-security-section">
              <div className="rounded-[18px] border border-border-subtle bg-card/72 p-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-slate-600">Confianza y cumplimiento</p>
              <h3 className="mt-3 text-2xl font-semibold text-text-heading">Seguridad visible, no promesas vacías.</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-700">No existe el “a prueba de hackers” absoluto, pero sí una base seria: pagos server-side, control por roles, protección anti abuso, endurecimiento del API y base legal reforzada para privacidad, cookies, tratamiento y conservación.</p>
              <div className="mt-6 rounded-[18px] border border-emerald-500/20 bg-emerald-500/8 p-5" data-testid="saas-security-score-card">
                <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300">Base de seguridad</p>
                <p className="mt-3 text-5xl font-bold text-text-heading">{securityPosture.security_score}/100</p>
                <p className="mt-2 text-sm text-slate-700">Puntuación orientativa interna basada en medidas activas del sistema y baseline legal/técnica actual.</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {securityPosture.controls?.map((control) => (
                <div key={control.id} className="rounded-[18px] border border-border-subtle bg-card/72 p-5" data-testid={`saas-security-control-${control.id}`}>
                  <div className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-primary">{control.status}</div>
                  <h4 className="mt-3 text-lg font-semibold text-text-heading">{control.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-slate-700">{control.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SaaSPricingHub;